import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { ArrowRight, HelpCircle } from "lucide-react";

export default function ProblemSelector() {
  const { t } = useLanguage();
  const [active, setActive] = useState(t.selector.problems[0].id);
  const current = t.selector.problems.find((p) => p.id === active) || t.selector.problems[0];

  return (
    <section className="py-24 bg-brand-subtle" data-testid="problem-selector">
      <div className="container-pad">
        <div className="max-w-2xl">
          <div className="overline">{t.selector.overline}</div>
          <h2 className="mt-3 text-4xl sm:text-5xl leading-[1.05]">
            {t.selector.title}
          </h2>
          <p className="mt-5 text-brand-text/70 text-lg leading-relaxed">
            {t.selector.subtitle}
          </p>
        </div>

        <div className="mt-14 grid gap-6 lg:grid-cols-[1.1fr_1fr]">
          <div className="space-y-3" data-testid="problem-list">
            {t.selector.problems.map((p) => {
              const isActive = p.id === active;
              return (
                <button
                  key={p.id}
                  onClick={() => setActive(p.id)}
                  data-testid={`problem-option-${p.id}`}
                  className={`w-full text-left flex items-start gap-4 rounded-lg border px-5 py-4 transition-all duration-300 ${
                    isActive
                      ? "border-brand-primary bg-brand-surface shadow-md -translate-y-0.5"
                      : "border-brand-line bg-brand-surface/60 hover:bg-brand-surface hover:border-brand-mute"
                  }`}
                >
                  <span
                    className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                      isActive
                        ? "bg-brand-primary text-brand-surface"
                        : "bg-brand-mute text-brand-primary"
                    }`}
                  >
                    {p.id.replace("p", "")}
                  </span>
                  <span
                    className={`font-medium leading-snug ${
                      isActive ? "text-brand-primary" : "text-brand-text/85"
                    }`}
                  >
                    {p.q}
                  </span>
                </button>
              );
            })}
          </div>

          <div
            className="relative rounded-xl bg-brand-primary text-brand-surface p-10 lg:p-12 overflow-hidden grain"
            data-testid="problem-answer-panel"
          >
            <HelpCircle className="absolute -top-6 -right-6 text-brand-accent/20" size={180} />
            <div className="relative">
              <div className="overline text-brand-accent">
                {t.selector.overline}
              </div>
              <h3 className="mt-4 font-serif text-2xl sm:text-3xl leading-tight text-brand-surface">
                {current.q}
              </h3>
              <p className="mt-6 text-brand-surface/80 text-base sm:text-lg leading-relaxed">
                {current.a}
              </p>
              <Link
                to={current.link_to}
                data-testid={`problem-cta-${current.id}`}
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-accent text-white px-6 py-3 text-sm font-semibold tracking-wide transition-transform duration-200 hover:translate-x-1"
              >
                {current.link_label}
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
